import { useContext, useState } from "react";
import { DataContext } from "../../contexts/DataProvider";
import {
  hydrateTemplateSections,
  normalizeMarkdown,
  type DocTemplateFormData,
  type DocTemplateListItem,
  type TemplateSection,
} from "../templateSerializer";

interface VersionEntry {
  _id: string;
  version: string;
  rawMarkdown: string;
  updatedAt: string;
  updatedBy: string;
  templateName: string;
  templateType: string;
}

interface UseVersionHistoryOptions {
  list: DocTemplateListItem[];
  selectedIndex: number | null;
  isGlobalAdmin: boolean;
  templateData: DocTemplateFormData;
  setAlert: (message: string, ...rest: any[]) => void;
  setSelectedIndex: (idx: number) => void;
  setPowerPointFile: (file: File | null) => void;
  setMonacoContent: (content: string) => void;
  setTemplateSections: (sections: TemplateSection[]) => void;
  setEditorMode: (mode: "sections" | "markdown") => void;
  setSectionsDirty: (dirty: boolean) => void;
  setTemplateData: (data: DocTemplateFormData) => void;
  sectionsParseSourceRef: React.MutableRefObject<string>;
}

/** Owns the version history drawer: which versions are shown, selected for diff, and restore. */
export const useVersionHistory = ({
  list,
  selectedIndex,
  isGlobalAdmin,
  templateData,
  setAlert,
  setSelectedIndex,
  setPowerPointFile,
  setMonacoContent,
  setTemplateSections,
  setEditorMode,
  setSectionsDirty,
  setTemplateData,
  sectionsParseSourceRef,
}: UseVersionHistoryOptions) => {
  const { login }: any = useContext(DataContext);
  const [versionDrawerOpen, setVersionDrawerOpen] = useState(false);
  const [versionHistory, setVersionHistory] = useState<VersionEntry[]>([]);
  const [currentVersionIdx, setCurrentVersionIdx] = useState(0);
  const [selectedVersionIdxs, setSelectedVersionIdxs] = useState<number[]>([]);

  const handleViewVersions = (templateId: string) => {
    const template = list.find((tpl) => tpl.templateId === templateId);
    if (!template) {
      setVersionHistory([]);
      setVersionDrawerOpen(true);
      return;
    }

    // Don't allow versioning for PowerPoint templates for now
    if (template.templateType === "powerpoint") {
      setAlert(
        "Version history is not available for PowerPoint templates.",
        "info",
      );
      return;
    }

    // Prevent org admins from viewing version history of templates created by global admin
    if (!isGlobalAdmin && template.templateUploadedBy === "global admin") {
      setAlert(
        "You cannot view version history of templates created by global admin. Only templates you created can have their version history viewed.",
        "error",
      );
      return;
    }

    const templateIndex = list.findIndex(
      (tpl) => tpl.templateId === templateId,
    );
    if (templateIndex !== -1 && templateIndex !== selectedIndex) {
      setSelectedIndex(templateIndex);
      setPowerPointFile(null);
      const content = template.rawMarkdown || template.templateContent || "";
      setMonacoContent(content);
      setTemplateSections(
        hydrateTemplateSections(content, template.sections as never),
      );
      setEditorMode("sections");
      setSectionsDirty(false);
      setTemplateData({
        templateName: template.templateName,
        templateType: template.templateType,
        templateContent: content,
        rawMarkdown: content,
      });
    }

    // Compose version history (current + last 4 historical)
    const currentVersion = {
      _id: "current",
      version: "current",
      rawMarkdown: template.rawMarkdown || template.templateContent || "",
      updatedAt: new Date().toISOString(),
      updatedBy: login?.data?.name || "Current User",
      templateName: template.templateName,
      templateType: template.templateType,
    };

    const historicalVersions = (template.versions || [])
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      )
      .slice(0, 4);

    const allVersions = [currentVersion, ...historicalVersions] as VersionEntry[];
    setVersionHistory(allVersions);
    setCurrentVersionIdx(0);
    setSelectedVersionIdxs([]);
    setVersionDrawerOpen(true);
  };

  const handleCloseVersionDrawer = () => {
    setVersionDrawerOpen(false);
    setVersionHistory([]);
    setSelectedVersionIdxs([]);
  };

  const handleCheckboxChange = (idx: number) => {
    setSelectedVersionIdxs((prev) => {
      if (prev.includes(idx)) {
        return prev.filter((i) => i !== idx);
      } else {
        return prev.length < 2 ? [...prev, idx] : prev;
      }
    });
  };

  const handleRestoreVersion = (version: VersionEntry) => {
    if (!version) return;
    if (version.templateType === "powerpoint") return;

    const restored = version.rawMarkdown || "";
    setMonacoContent(restored);
    setTemplateSections(hydrateTemplateSections(restored, undefined));
    sectionsParseSourceRef.current = normalizeMarkdown(restored);
    setEditorMode("sections");
    setSectionsDirty(false);
    setPowerPointFile(null);

    if (selectedIndex !== null && list[selectedIndex]) {
      const currentTemplate = list[selectedIndex];
      setTemplateData({
        templateName: currentTemplate.templateName,
        templateType: currentTemplate.templateType,
        templateContent: version.rawMarkdown || "",
        rawMarkdown: version.rawMarkdown || "",
      });
    } else {
      setTemplateData({
        templateName: version.templateName || templateData.templateName,
        templateType: version.templateType || templateData.templateType,
        templateContent: version.rawMarkdown || "",
        rawMarkdown: version.rawMarkdown || "",
      });
    }

    setVersionDrawerOpen(false);
  };

  return {
    versionDrawerOpen,
    versionHistory,
    currentVersionIdx,
    selectedVersionIdxs,
    handleViewVersions,
    handleCloseVersionDrawer,
    handleCheckboxChange,
    handleRestoreVersion,
  };
};
